/**
 * One live region, several parts of the screen that can each have something to say.
 *
 * `/konto` has four: the Profil tab's saves, the brand colour's, the sentence the checkout's
 * return flow puts under the seats, and the Google calendar connection's. They were joined with
 * `||`, and that is the defect this exists to fix — `useAnnouncer` deliberately never clears, so the FIRST source to speak owned the region
 * for the rest of the session and everything said after it was announced to nobody. A trainer who
 * saved a name in the morning never heard „Aktywujemy Twoją subskrypcję…" at all.
 *
 * The rule instead: the region says whatever CHANGED most recently, and a source falling silent is
 * not a change worth announcing. `useAnnouncer` itself is untouched — its other eleven callers own
 * one region each, where a precedence question does not arise — and the repeat wrinkle it carries
 * is inherited here, so „Zapisano" twice in a row is still read out twice.
 */

import { useEffect, useRef } from 'react';

import { useAnnouncer } from '~/shared/lib/useAnnouncer';

/**
 * @param sources every message on the screen, in a STABLE order — the position is the identity.
 * @returns what the screen's one `role="status"` region should be rendering.
 */
export function useLatestAnnouncement(sources: readonly string[]): string {
  const { message, announce } = useAnnouncer();
  /* `null` until the first run, which is what keeps a message that was already on screen when this
   * mounted from being announced as though it had just happened. */
  const previous = useRef<readonly string[] | null>(null);

  useEffect(() => {
    const before = previous.current;
    previous.current = sources;
    if (before === null) return;

    const said = sources.find((text, index) => text !== before[index]);
    // An empty source is a message being withdrawn, not a new one. There is nothing to read out.
    if (said !== undefined && said !== '') announce(said);
    // `sources` is rebuilt per render, so this runs per render and the comparison above is the
    // whole guard — three string compares, and `announce` is stable by contract.
  }, [sources, announce]);

  return message;
}
